import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapPatient } from '@/api/mappers';
import { patientQueries } from '@/features/patient-record/queries';
import type { PatientViewModel } from '@/models/patient';

/**
 * Sensitive-flag lifecycle (PTV2-1211 / S4 FE). Split into two mutations
 * so the toggle-on flow (reason required) and toggle-off flow (no body)
 * stay type-safe at the call site.
 *
 *  - `set`   PUT /persons/{guid}/sensitive with a non-blank reason.
 *  - `clear` DELETE /persons/{guid}/sensitive.
 *
 * Both write back the patient view-model on settle so the sensitive block
 * and any header banner (FE-S5) render the newest state without a manual
 * refetch.
 */
export function useSensitivityMutations(guid: string): {
  readonly set: UseMutationResult<PatientViewModel, Error, string>;
  readonly clear: UseMutationResult<undefined, Error, undefined>;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.detail(guid);
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const set = useMutation<PatientViewModel, Error, string>({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: async (reason) => {
      const dto = await apiClient.setSensitive(guid, { reason });
      return mapPatient(dto);
    },
    onSuccess: (patient) => {
      queryClient.setQueryData(queryKey, patient);
    },
    onSettled: invalidate,
  });

  const clear = useMutation<undefined, Error, undefined>({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: () => apiClient.clearSensitive(guid),
    onSettled: invalidate,
  });

  return { set, clear };
}
